import ko from "knockout";
import { IncontactApiError } from "../api/components/IncontactApiError.js";
import { IncontactAPI } from "../api/incontact-api.js";
import { getBaseUrl } from "../utils/index.js";
import pino from "pino";
import config from "../config.js";

const logger = pino({
  level: "debug",
  transport: {
    target: "pino-pretty",
    options: {
      colorize: true,
    },
  },
});

const DEFAULT_CONFIG = {
  auth: {
    returnUrl: getDefaultReturnUrl(),
    issuer: IncontactAPI.DEFAULT_ISSUER,
    userHub: false,
  },
};

function getDefaultReturnUrl() {
  return normalizeReturnUrl(`${getBaseUrl()}/pages/auth/accept.html`);
}

function normalizeReturnUrl(url) {
  if (!url) {
    return url;
  }
  try {
    const origin =
      typeof window !== "undefined" && window.location
        ? window.location.origin && window.location.origin !== "null"
          ? window.location.origin
          : `${window.location.protocol}//${window.location.host}`
        : undefined;
    const parsed = origin ? new URL(url, origin) : new URL(url);
    parsed.pathname = parsed.pathname.replace(/\/{2,}/g, "/");
    return parsed.toString();
  } catch (_) {
    const match = url.match(/^(https?:\/\/)(.*)$/i);
    if (match) {
      const [, scheme, rest] = match;
      return `${scheme}${rest.replace(/\/{2,}/g, "/")}`;
    }
    return url.replace(/\/{2,}/g, "/");
  }
}

export class LoginViewModel {
  constructor(onSystemError) {
    this.statusMessageTimer = null;
    this.userName = ko.observable("");
    this.logVM = null;
    this.isAuthenticated = ko.observable(false);
    this.isSessionStarted = ko.observable(false);
    this.isSessionJoined = null;
    this.errorMessage = ko.observable();
    this.errorMessageVisible = ko.observable(false);
    this.errorMessageType = ko.observable();
    this.enableLoginBtn = ko.observable(true);
    this.config = ko.observable({});
    this.incontact = null;
    this.supervisorIncontact = null;
    this.isSupervisor = ko.observable(false);
    this.onSystemError = onSystemError;
    this.configLoaded = false;
    // collapse state for user banner section
    this.isUserCollapsed = ko.observable(false);
  }
  // Toggle collapse/expand of the user banner section
  toggleUser() {
    this.isUserCollapsed(!this.isUserCollapsed());
  }

  async handleLoadConfig() {
    if (this.configLoaded) {
      logger.log("Configuration already loaded. Skipping reload.");
      return;
    }
    this.setStatusMessage("Loading configuration...", false, false);
    try {
      const cfg = config;
      this.setConfigDefaults(cfg);
      this.config(cfg);
      this.handleMonitorConfig(cfg.monitor);
      this.handleDataDogConfig(cfg.dataDog);
      this.clearStatusMessage();
      this.configLoaded = true;
      logger.log("Configuration loaded successfully:", cfg);
      return cfg;
    } catch (error) {
      logger.error("Failed to load startup configuration:", error);
      this.handleError(error, "Failed to load startup configuration.");
      throw error;
    }
  }
  __validateAuthenticationFields() {
    return true;
  }

  setConfigDefaults(data) {
    logger.info("Setting configuration defaults.");
    data.auth = { ...DEFAULT_CONFIG.auth, ...(data.auth || {}) };
    data.auth.returnUrl = normalizeReturnUrl(data.auth.returnUrl);
  }

  // Removed runtime fetchConfig; configuration is loaded from build-time JSON

  // Removed runtime fetchConfig; configuration is loaded from build-time JSON

  handleMonitorConfig(monitorConfig) {
    if (monitorConfig) {
      logger.log("Handling Monitoring configuration.");
      sessionStorage.setItem("supervisorProfiles", monitorConfig.supervisor_roles || "");
      sessionStorage.setItem("includeLanguageSkills", monitorConfig.includeLanguageSkills || "");
      sessionStorage.setItem("callRefreshWindow", monitorConfig.callRefreshWindow || "");
      sessionStorage.setItem(
        "supervisorPageTitle",
        monitorConfig.supervisorPageTitle || "ServiceMonitor Monitoring",
      );
      sessionStorage.setItem(
        "sessionPageTitle",
        monitorConfig.sessionPageTitle || "ServiceMonitor Monitoring Session",
      );
    }
  }

  handleDataDogConfig(dataDogConfig) {
    if (dataDogConfig) {
      logger.log("Handling DataDog configuration.");
      if (dataDogConfig.disabled !== undefined) {
        sessionStorage.setItem("disableDataDog", String(Boolean(dataDogConfig.disabled)));
      } else {
        sessionStorage.removeItem("disableDataDog");
      }
      if (Array.isArray(dataDogConfig.logLevels) && dataDogConfig.logLevels.length > 0) {
        sessionStorage.setItem("dataDogLogLevels", dataDogConfig.logLevels.join(","));
      } else {
        sessionStorage.removeItem("dataDogLogLevels");
      }
      if (dataDogConfig.clientToken) {
        sessionStorage.setItem("dataDogClientToken", dataDogConfig.clientToken);
      } else {
        sessionStorage.removeItem("dataDogClientToken");
      }
    }
  }

  get title() {
    const defaultTitle = "ServiceMonitor Interpreter Client";
    const currentConfig = this.config();
    return currentConfig &&
      Object.keys(currentConfig).length > 0 &&
      currentConfig.title !== undefined
      ? currentConfig.title
      : defaultTitle;
  }

  get loginTextHeader() {
    return "Log In";
  }

  setLogger(logger) {
    this.logVM = logger;
  }

  async getAPI(isSupervisor = false) {
    // Ensure startup configuration is loaded
    await this.handleLoadConfig();
    let api = isSupervisor ? this.supervisorIncontact : this.incontact;
    const prefix = isSupervisor ? "supervisor" : "interpreter";
    if (!api) {
      api = new IncontactAPI(this.config().auth, prefix);
      if (isSupervisor) {
        this.supervisorIncontact = api;
      } else {
        this.incontact = api;
      }
    }
    return api;
  }

  async getInterpreterIncontactAPI() {
    return this.getAPI(false);
  }

  async getSupervisorIncontactAPI() {
    return this.getAPI(true);
  }

  async redirectToAuthProvider() {
    this.setStatusMessage("Looking up login page...", false, false);
    if (!this.__validateAuthenticationFields()) {
      logger.log("form is not ready to submit");
      return;
    }
    localStorage.setItem("username", this.userName());
    this.isAuthenticated(false);
    this.enableLoginBtn(false);
    this.setStatusMessage(
      "Looking up login page for " + this.userName() + ".  Please wait...",
      false,
      false,
    );

    const api = this.isSupervisor()
      ? await this.getSupervisorIncontactAPI()
      : await this.getInterpreterIncontactAPI();
    api.doDiscovery().then((endpoints) => {
      this.setStatusMessage(
        "Found login page of " + endpoints.authorizationEndpoint + ".  redirecting...",
        false,
        false,
      );
      sessionStorage.setItem("service-monitor.username", this.userName());
      sessionStorage.setItem("returnPage", window.location.href);
      window.location.href = `${endpoints.authorizationEndpoint}?scope=openid&response_type=code&client_id=${encodeURIComponent(this.config().auth.clientId)}&login_hint=${encodeURIComponent(this.userName())}&redirect_uri=${encodeURIComponent(this.config().auth.returnUrl)}`;
    });
  }

  async finishLogin(code, pollEvents) {
    try {
      const api = this.isSupervisor()
        ? await this.getSupervisorIncontactAPI()
        : await this.getInterpreterIncontactAPI();
      await api.authenticateWithCode(code);
      await this.startSession();
      setTimeout(pollEvents, 1);
    } catch (error) {
      this.handleError(error, "Failed to finish login.");
    }
  }

  async manageSession(action) {
    try {
      const api = this.isSupervisor()
        ? await this.getSupervisorIncontactAPI()
        : await this.getInterpreterIncontactAPI();
      const result = await action(api);
      return result;
    } catch (error) {
      this.handleError(error, `Failed to ${action.name} session.`);
      throw error;
    }
  }

  async startSession() {
    return this.manageSession(async (api) => {
      this.setStatusMessage("Starting session...", false, false);
      const data = await api.startSession("WebRTC", null);
      this.isSessionStarted(true);
      this.clearStatusMessage();
      return data;
    });
  }

  handleSessionError(error, api, accept, reject) {
    // Always delegate error handling to base handler; API will return sessionId on 409 SessionInProgress
    this.handleError(
      error,
      `Error starting session for ${this.userName()}: ${error.statusCode} ${error.statusDescription}`,
    );
    reject(new IncontactApiError(error.responseCode, error.statusDescription));
  }

  async endSession() {
    this.setStatusMessage(
      "Ending session for " + this.userName() + ".  Please wait...",
      false,
      false,
    );

    const incontactApi = this.isSupervisor()
      ? await this.getSupervisorIncontactAPI()
      : await this.getInterpreterIncontactAPI();
    incontactApi
      .endSession(true, false, false)
      .then((url) => {
        this.handleEndSessionSuccess(url);
      })
      .catch((error) => {
        this.handleEndSessionError(error);
      });
  }

  handleEndSessionSuccess(url) {
    try {
      logger.log("received url " + url);
      this.enableLoginBtn(true);
      this.clearStatusMessage();
      const returnPage = "https://nice.incontact.com";
      localStorage.setItem("returnPage", returnPage);
      let logoutUrl = window.location.href;
      const pos = logoutUrl.indexOf("/", "https://".length + 1);
      if (pos > 0) {
        logoutUrl = this.config().auth.returnUrl.substring(0, pos) + "/auth/logout.html";
      }
      sessionStorage.clear();
      window.location.href = `${url}&post_logout_redirect_uri=${encodeURIComponent(logoutUrl)}`;
    } catch (error) {
      logger.trace("error: " + error);
    }
  }

  handleEndSessionError(error) {
    logger.trace("Received error: " + JSON.stringify(error));
    if (error.statusCode === 409) {
      this.setStatusMessage(
        "Unable to end session for " + this.userName() + ": " + error.statusDescription + ".",
      );
    } else {
      sessionStorage.clear();
      window.location.href = "/";
    }
  }

  setStatusMessage(msg, doTimeout = true, isError = true, logMessage = false) {
    this.errorMessage(msg);
    this.errorMessageVisible(true);
    this.errorMessageType(isError ? "error" : "status");
    isError
      ? logger.error("Status message set to: " + msg)
      : logger.info("Status message set to: " + msg);

    if (this.statusMessageTimer) {
      clearTimeout(this.statusMessageTimer);
      this.statusMessageTimer = null;
    }

    if (doTimeout) {
      if (logMessage && this.logVM) {
        this.logVM.addLog(msg);
      }
      this.statusMessageTimer = setTimeout(() => {
        this.clearStatusMessage();
        this.statusMessageTimer = null;
      }, 10000);
    }
  }

  clearStatusMessage() {
    this.errorMessage("");
    this.errorMessageVisible(false);
  }

  doReload(state, error) {
    let hideLoginScreen = true;
    switch (state) {
      case "attemptingToConnect":
        this.setStatusMessage(
          "Attempting to restore session for " + this.userName() + ".  Please wait...",
          false,
          false,
        );
        break;
      case "showSessionStarted":
        this.clearStatusMessage();
        break;
      case "showLoginScreen":
        hideLoginScreen = false;
        if (error) {
          this.setStatusMessage(
            "Error restoring session for " +
              this.userName() +
              ".  " +
              error.statusDescription +
              ".",
          );
        }
        break;
    }
    this.isSessionStarted(hideLoginScreen);
    this.isAuthenticated(hideLoginScreen);
    this.enableLoginBtn(!hideLoginScreen);
  }

  clear() {
    this.isSessionStarted(false);
    this.isAuthenticated(false);
    this.isSessionJoined = false;
  }

  getQueryVariable(variable) {
    const query = window.location.search.substring(1);
    const vars = query.split("&");
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split("=");
      if (pair[0] === variable) {
        return pair[1];
      }
    }
    return false;
  }

  handleError(error, customMessage) {
    logger.error("Error:", error);
    const message = customMessage || "An unexpected error occurred.";
    this.setStatusMessage(message, true, true);
    throw error; // Propagate the error for upstream handling
  }
}
